import {
  BookOpenIcon,
  GlobeIcon,
  GraduationCapIcon,
  GrapeIcon,
  LandmarkIcon,
  LeafIcon,
  UtensilsCrossedIcon,
  WarehouseIcon,
  WineIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Foundations of Wine & Oenology: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    classroom:
      'Your first introduction to wine studies can be transformative or tedious. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'What is wine and why it matters',
      'The wine industry and its structure',
      'The relationship between viticulture and winemaking',
      'Grape composition and chemistry',
      'Fundamental wine science',
      'Major production concepts',
      'Wine styles and classifications',
      'Quality factors',
      'Professional terminology',
      'The complete journey from vineyard to consumer',
    ],
    result:
      'Your students understand wine as a science, business, and cultural phenomenon—not an impossible list of terms to memorize.',
    icon: WineIcon,
  },
  {
    number: '2',
    title: 'Viticulture: From Vine to Grape',
    subtitle: 'Everything your students need to know about grape growing and how vineyard decisions influence wine',
    classroom:
      "This is where many students struggle to connect vineyard practices to final wine characteristics. This volume gives you the materials to help them truly understand viticulture's importance.",
    items: [
      'Grapevine biology and anatomy',
      'Vineyard establishment and development',
      'Climate and its influence on grapes',
      'Soils and their characteristics',
      'Terroir and its components',
      'Vineyard management practices',
      'Canopy management techniques',
      'Irrigation and water management',
      'Nutrition and fertilization',
      'Pests, diseases, and integrated management',
      'Grape maturity and sugar development',
      'Harvest decisions and timing',
      'Sustainability in viticulture',
      'How vineyard conditions influence grape composition',
      '12 practical viticulture case studies',
    ],
    result:
      'Your students learn how vineyard decisions directly influence grape quality and eventual wine characteristics.',
    icon: LeafIcon,
  },
  {
    number: '3',
    title: 'Winemaking Science & Production',
    subtitle: 'How grapes are transformed into wine through chemistry and technique',
    classroom:
      'Winemaking science is complex but fascinating. This volume gives you everything needed so your students understand each production decision and its consequences.',
    items: [
      'Grape reception and quality assessment',
      'Crushing and pressing techniques',
      'Fermentation: principles and science',
      'Yeast and microorganism selection',
      'Red wine production process',
      'White wine production process',
      'Rosé wine production',
      'Clarification and stabilization',
      'Quality management and monitoring',
      'Winery operations and workflow',
      'Common production challenges',
      'Problem-solving in winemaking',
      'How production decisions influence wine style',
      '15 worked winemaking science examples',
    ],
    result:
      "Your students understand the relationship between production choices and the final wine's characteristics.",
    icon: GrapeIcon,
  },
  {
    number: '4',
    title: 'Wine Aging, Cellaring & Development: Evolution Over Time',
    subtitle: 'What happens to wine after production and how it develops',
    classroom:
      'Wine development fascinates students but is often poorly explained. This volume connects storage conditions to sensory change.',
    items: [
      'Maturation and aging processes',
      'Barrel aging: oak types and influences',
      'Bottle aging and chemical evolution',
      'Oxygen exposure and its effects',
      'Storage conditions and their importance',
      'Temperature, humidity, and light control',
      'Cellar management best practices',
      'Closure types and their implications',
      'Cork, screw cap, and synthetic closures',
      'Sensory evolution during aging',
      'Aging potential and longevity',
      'Storage problems and prevention',
      'Common faults and their causes',
      'Aging timelines for different wine styles',
      '10 practical cellar management case studies',
    ],
    result:
      'Your students understand how storage conditions and time transform wine and develop its complexity.',
    icon: WarehouseIcon,
  },
  {
    number: '5',
    title: 'Sensory Analysis & Wine Tasting: Professional Evaluation',
    subtitle: 'A structured and educational approach to evaluating wine sensorially',
    classroom:
      'Tasting is often taught without methodology. This volume makes sensory analysis systematic and professional.',
    items: [
      'Sensory evaluation fundamentals',
      'Visual analysis: color, clarity, viscosity',
      'Aromatic evaluation: primary, secondary, tertiary aromas',
      'Taste evaluation: sweetness, acidity, tannin, alcohol, body',
      'Sensory vocabulary and descriptors',
      'Aroma families and classification',
      'Common wine faults and their identification',
      'Balanced vs. unbalanced wines',
      'Finish and persistence evaluation',
      'Comparative tasting methodology',
      'Blind evaluation concepts',
      'Professional communication of observations',
      'Tasting notes and documentation',
      'Individual differences in perception',
      '12 structured tasting activities',
    ],
    result:
      'Your students develop professional sensory evaluation skills and learn to communicate wine characteristics with precision.',
    icon: BookOpenIcon,
  },
  {
    number: '6',
    title: 'Wine Regions, Styles & Global Production: The World of Wine',
    subtitle: 'The international wine world through geography, climate, grapes, and tradition',
    classroom:
      'Understanding regional wine styles requires connecting geography to production. This volume does this systematically.',
    items: [
      'Wine regions and their classification',
      'Climate zones and their influence on wine',
      'Grape varieties and their regional expressions',
      'Production traditions and their origins',
      'European wine regions and their characteristics',
      'New World wine regions and their development',
      'Emerging wine regions',
      'Controlled designation systems',
      'How terroir creates distinct wine identities',
      'Regional wine styles and their evolution',
      'The role of tradition and innovation',
      'Consumer preferences by region',
      '15 global wine region case studies',
    ],
    result:
      'Your students understand why wines differ across regions and can analyze how environmental and cultural factors shape wine identity.',
    icon: GlobeIcon,
  },
  {
    number: '7',
    title: 'History, Culture & Society of Wine: Wine in Civilization',
    subtitle: 'Wine as a historical, cultural, economic, and social phenomenon',
    classroom: "Wine's role extends far beyond the bottle. This volume connects wine to human civilization.",
    items: [
      'Ancient wine development and early civilizations',
      'Medieval and Renaissance wine traditions',
      'Colonial wine expansion',
      "The Industrial Revolution's impact on wine",
      'Modern wine industry development',
      'Wine and religion',
      'Wine and gastronomy',
      'Wine trade and economics',
      'Wine and regional identity',
      'Wine and social customs',
      'Wine and gender roles (historical and contemporary)',
      'Ethical and sustainability issues in wine',
      'Contemporary wine consumer behavior',
      "Wine's role in tourism and experience economy",
      '10 wine history and culture case studies',
    ],
    result:
      'Your students understand wine as more than a beverage—as a reflection of human culture, history, and values.',
    icon: LandmarkIcon,
  },
  {
    number: '8',
    title: 'Wine Service, Food Pairing & Hospitality: Professional Practice',
    subtitle: 'Professional wine service and the connection between wine and food',
    classroom: "This is the practical heart. Your students learn wine's application in professional hospitality.",
    items: [
      'Wine service fundamentals',
      'Glassware selection and function',
      'Proper serving temperatures',
      'Wine storage for service',
      'Opening and presentation techniques',
      'Pouring and service procedures',
      'Guest communication and recommendation',
      'Wine menu design and strategy',
      'Food-and-wine pairing principles',
      'Pairing by flavor intensity',
      'Pairing by complementarity and contrast',
      'Acidity and its role in pairing',
      'Tannin interactions with food',
      'Sweetness and umami considerations',
      'Creating pairing menus',
      'Responsible service practices',
      'Cultural sensitivity in hospitality',
      'Professional communication skills',
      '12 practical hospitality case studies',
    ],
    result:
      'Your students are prepared for professional wine service roles and understand how wine enhances the dining experience.',
    icon: UtensilsCrossedIcon,
  },
  {
    number: '9',
    title: 'Teaching Wine Studies & Oenology: Classroom Excellence',
    subtitle: 'Your complete guide as a wine education professional',
    classroom:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of wine studies teaching.',
    items: [
      'The educational value of wine studies in professional curricula',
      'Establishing clear learning objectives',
      'Organizing wine content by difficulty level',
      'Designing effective lesson sequences',
      'Inquiry-based wine education approaches',
      'Problem-based and project-based learning strategies',
      'Using real wine case studies in the classroom',
      'Teaching through wine tasting experiences',
      'Integrating technology and digital resources',
      'Interdisciplinary connections and links',
      'Classroom discussions and guided debates',
      'Laboratory activities and sensory evaluation',
      'Field activities and winery visits',
      'Assessment strategies for wine learning',
      'Rubrics and performance criteria',
      'Addressing common wine misconceptions',
      'Differentiation and inclusive instruction',
      '10 complete integrated wine studies projects',
    ],
    result:
      'You\u2019ll never wonder “what do I teach today?” again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
